import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/toast/ToastProvider';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { Button } from '../ui/Button';

interface PaymentButtonsProps {
  onCancel: () => void;
}

interface PlanPricing {
  priceMonthly: number;
  priceYearly: number;
  name: string;
}

export default function PaymentButtons({ onCancel }: PaymentButtonsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [mpLoading, setMpLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [planData, setPlanData] = useState<PlanPricing | null>(null);
  const [loading, setLoading] = useState(true);

  // Cargar precios del plan desde Firestore
  useEffect(() => {
    loadPlanPricing();
  }, []);

  const loadPlanPricing = async () => {
    try {
      const planDoc = await getDoc(doc(db, 'subscriptionPlans', 'familiar'));
      
      if (planDoc.exists()) {
        const data = planDoc.data();
        setPlanData({
          name: data.name || 'Plan Familiar',
          priceMonthly: data.priceMonthly || data.price || 9.99,
          priceYearly: data.priceYearly || data.priceMonthly * 10 || 95.99,
        });
      } else {
        console.error('Plan familiar no encontrado en Firestore');
        // Fallback a valores por defecto
        setPlanData({
          name: 'Plan Familiar',
          priceMonthly: 9.99,
          priceYearly: 95.99,
        });
      }
    } catch (error) {
      console.error('Error cargando precios del plan:', error);
      // Fallback a valores por defecto
      setPlanData({
        name: 'Plan Familiar',
        priceMonthly: 9.99,
        priceYearly: 95.99,
      });
    } finally {
      setLoading(false);
    }
  };

  // Start Mercado Pago checkout
  const startMpCheckout = useCallback(async () => {
    try {
      if (!user?.uid) return;
      setMpLoading(true);
      const functions = getFunctions();
      const createPref = httpsCallable(functions, 'createMpPreapproval');
      const interval = billingInterval === 'yearly' ? 'year' : 'month';
      const res: any = await createPref({ plan: 'familiar', interval });
      const url = res?.data?.init_point || res?.data?.sandbox_init_point;
      if (url) {
        // Usar navegación especial para pagos que abre navegador externo en app nativa
        import('../../utils/navigation').then(({ navigateToPayment }) => {
          navigateToPayment(String(url));
        });
      } else {
        showToast({ type: 'error', message: 'No se pudo iniciar el pago' });
      }
    } catch (e) {
      showToast({ type: 'error', message: 'No se pudo iniciar el pago' });
    } finally {
      setMpLoading(false);
    }
  }, [user?.uid, billingInterval, showToast]);

  // Calcular ahorro y precio mensual del plan anual
  const monthlyEquivalent = planData ? (planData.priceYearly / 12).toFixed(2) : '0.00';
  const savingsPercent = planData 
    ? Math.round((1 - (planData.priceYearly / 12) / planData.priceMonthly) * 100)
    : 0;

  // Mostrar loading mientras se cargan los precios
  if (loading || !planData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('paymentButtons.loadingOptions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-6xl mb-4">💳</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('paymentButtons.renewPlan')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('paymentButtons.chooseMethod')}
        </p>
      </div>

      {/* Billing Interval Selector */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setBillingInterval('monthly')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              billingInterval === 'monthly'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <div className="text-sm">{t('paymentButtons.monthly')}</div>
            <div className="text-lg font-bold">${planData.priceMonthly.toFixed(2)}/{t('paymentButtons.month')}</div>
          </button>
          
          <button
            onClick={() => setBillingInterval('yearly')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all relative ${
              billingInterval === 'yearly'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {savingsPercent > 0 && (
              <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                {t('paymentButtons.save')} {savingsPercent}%
              </div>
            )}
            <div className="text-sm">{t('paymentButtons.yearly')}</div>
            <div className="text-lg font-bold">${planData.priceYearly.toFixed(2)}/{t('paymentButtons.year')}</div>
            <div className="text-xs opacity-75">(${monthlyEquivalent}/mes)</div>
          </button>
        </div>
      </div>

      {/* Benefits Reminder */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border-2 border-blue-200 dark:border-blue-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <span>✨</span>
          <span>{t('paymentButtons.recoverWith', { name: planData.name })}</span>
        </h3>
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li className="flex items-center gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>{t('paymentButtons.benefit1')}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>{t('paymentButtons.benefit2')}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>{t('paymentButtons.benefit3')}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>{t('paymentButtons.benefit4')}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500 font-bold">✓</span>
            <span>{t('paymentButtons.benefit5')}</span>
          </li>
        </ul>
      </div>

      {/* Payment Button */}
      <div className="space-y-3">
        <Button
          onClick={startMpCheckout}
          disabled={mpLoading}
          className="w-full bg-[#009ee3] hover:bg-[#0089cc] text-white py-4 text-lg font-semibold flex items-center justify-center gap-3"
        >
          {mpLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>{t('paymentButtons.processing')}</span>
            </>
          ) : (
            <>
              <span>💳</span>
              <span>{t('paymentButtons.payWithMP')}</span>
            </>
          )}
        </Button>

        <Button
          onClick={onCancel}
          variant="outline"
          className="w-full border-2 border-gray-300 dark:border-gray-600 py-3"
        >
          {t('paymentButtons.backToWizard')}
        </Button>
      </div>

      {/* Security Message */}
      <p className="text-xs text-center text-gray-500 dark:text-gray-400">
        🔒 {t('paymentButtons.securePayment')}
      </p>
    </div>
  );
}
